'use client'

import { useCallback, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Icons } from '@/components/ui/icons'
import { Skeleton } from '@/components/ui/skeleton'
import { apiClient } from '@/lib/api-client'
import { appendScopeFilterParams } from '@/lib/dashboard-scope-filters'
import { cn } from '@/lib/utils'

interface TenureBucket {
  label: string
  agent_count: number
  call_count: number
  avg_score: number | null
}

interface OverallTenureData {
  buckets: TenureBucket[]
  total_agents: number
  ai_insight: string | null
}

interface Props {
  startDate: string
  endDate: string
  targetEmployeeId?: number
  selectedProduct?: string
  selectedSite?: string
}

function scoreColor(score: number | null) {
  if (score === null) return 'text-gray-400'
  if (score >= 85) return 'text-emerald-600'
  if (score >= 70) return 'text-blue-600'
  if (score >= 55) return 'text-amber-600'
  return 'text-red-600'
}

function scoreBg(score: number | null) {
  if (score === null) return 'bg-gray-50 border-gray-200'
  if (score >= 85) return 'bg-emerald-50 border-emerald-200'
  if (score >= 70) return 'bg-blue-50 border-blue-200'
  if (score >= 55) return 'bg-amber-50 border-amber-200'
  return 'bg-red-50 border-red-200'
}

export function TenureWiseOverviewCard({
  startDate,
  endDate,
  targetEmployeeId,
  selectedProduct,
  selectedSite,
}: Props) {
  const [data, setData] = useState<OverallTenureData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!startDate || !endDate) return
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate })
      if (targetEmployeeId) params.set('target_employee_id', String(targetEmployeeId))
      appendScopeFilterParams(params, selectedProduct, selectedSite)
      const res = await apiClient.get<OverallTenureData>(
        `/api/analysis/tenure-score/overall?${params}`
      )
      setData(res)
    } catch {
      setError('Unable to load tenure data')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, targetEmployeeId, selectedProduct, selectedSite])

  useEffect(() => {
    load()
  }, [load])

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <Card className='relative overflow-hidden'>
        <CardWatermark opacity={2} />
        <CardHeader className='relative z-10'>
          <CardTitle className='flex items-center gap-2'>
            <Icons.trendingUp className='h-5 w-5 text-brand-cornflower' strokeWidth={1.5} />
            Tenure Wise Score
          </CardTitle>
          <p className='text-sm text-muted-foreground mt-1'>
            Avg of agent tenure scores · {data?.total_agents ?? 0} agent
            {(data?.total_agents ?? 0) !== 1 ? 's' : ''} with DOJ mapping
          </p>
        </CardHeader>
        <CardContent className='relative z-10'>
          {loading ? (
            <div className='grid grid-cols-2 gap-4 sm:grid-cols-4'>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className='h-24 rounded-xl' />
              ))}
            </div>
          ) : error || !data ? (
            <p className='text-center py-6 text-muted-foreground text-sm'>
              {error ?? 'No tenure data available'}
            </p>
          ) : (
            <div className='space-y-4'>
              <div className='grid grid-cols-2 gap-4 sm:grid-cols-4'>
                {data.buckets.map((bucket) => (
                  <div
                    key={bucket.label}
                    className={cn(
                      'rounded-xl border-2 p-4 text-center transition-all',
                      scoreBg(bucket.avg_score)
                    )}
                  >
                    <p className='text-xs font-medium text-muted-foreground mb-2'>
                      {bucket.label}
                    </p>
                    {bucket.agent_count === 0 ? (
                      <p className='text-2xl font-bold text-gray-300'>N/A</p>
                    ) : (
                      <p className={cn('text-3xl font-bold', scoreColor(bucket.avg_score))}>
                        {bucket.avg_score}%
                      </p>
                    )}
                    <p className='text-xs text-muted-foreground mt-1'>
                      {bucket.agent_count} agent{bucket.agent_count !== 1 ? 's' : ''}
                      {bucket.call_count > 0 ? ` · ${bucket.call_count} calls` : ''}
                    </p>
                  </div>
                ))}
              </div>
              {data.ai_insight && (
                <div className='rounded-lg bg-brand-cornflower/5 border border-brand-cornflower/20 p-4'>
                  <p className='text-xs font-semibold text-brand-cornflower mb-1 uppercase tracking-wide'>
                    AI Insight
                  </p>
                  <p className='text-sm text-gray-700 leading-relaxed'>{data.ai_insight}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  )
}
